import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../../api/client';
import { LookupCombo, type LookupData } from '../../../components/LookupCombo';
import { ComboSelect } from '../../../components/ComboSelect';
import { CheckedCombo } from '../../../components/CheckedCombo';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { LoaderSymbol, runLoading } from '../../../components/loading';
import { serverMessage } from '../../../components/confirm/serverMessage';
import { hasUnsavedChanges, markUserEdit, resetUnsavedTracking } from '../../../hooks/useUnsavedGuard';
import { FormTitle } from '../../../components/FormTitle';
import { FormReportsMenu } from '../../../components/FormReportsMenu';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { navigateToPage } from '../../../layouts/navigateTo';
import { useNavActions } from '../../../hooks/useNavActions';
import '../../backlog/backlog.css';
import '../../sampling/sampleRegistration/sampleRegistration.css';
import './preLoginSettings.css';
import PreLoginSamplesPage from './PreLoginSamplesTab';
import PreLoginDocsPanel from './PreLoginDocsPanel';
import PreLoginScheduleDialog from './PreLoginScheduleDialog';
import SpecialNotesDialog from './SpecialNotesDialog';
import { usePreLoginSchedules } from './usePreLoginSchedules';
import type { PreLoginDetail, PreLoginInput, PreLoginLookup, LookupKind } from './types';

/** The audit FormName — registered in CLAUDE.md §14b.2. */
export const PRE_LOGIN_FORM = 'Pre-Login Settings';

/** The three collections that open as a SEPARATE PAGE — the legacy screen's
 *  bottom Samples / Tests / Containers buttons. Attachment and Note are NOT
 *  here: they stay on the form as a tabbed control, exactly as the legacy
 *  screen has them. Mirrors Sample Registration, whose Sample Login / Tests /
 *  Containers steps likewise replace the registration form with their own page. */
export type ChildTab = 'samples' | 'tests' | 'containers' | 'parameters';

/** What to open once an unsaved record has been saved — either one of the child
 *  collection PAGES or the Task Scheduler POPUP. The page is keyed on the oid,
 *  so a first save remounts it and this has to travel through the parent. */
export type PostSaveTarget = ChildTab | 'schedule';

/** EDDFormat is an int enum on the BO; these are its members in order. */
const EDD_FORMATS = ['None', 'Excel', 'CSV', 'XML', 'Text'];

interface Props {
  /** null = New. */
  oid: string | null;
  /** The list's current page, so prev/next walk the same order the user sees. */
  siblingIds?: string[];
  onClose: () => void;
  /** `openAfter` carries "and then open this" across the remount a first save
   *  causes (the page is keyed on the oid). The parent hands it straight back
   *  as `initialTarget`. */
  onNavigate?: (oid: string, openAfter?: PostSaveTarget) => void;
  onSaved?: () => void;
  /** Set by the parent when this mount is the result of "save, then open X". */
  initialTarget?: PostSaveTarget;
}

/** An empty header — the shape a New record starts from. */
const BLANK: PreLoginInput = {
  cocName: null, clientOid: null, clientContactOid: null, email: null,
  projectOid: null, projectManagerOid: null, projectCategoryOid: null,
  projectSource: null, projectOverview: null, projectCity: null,
  permitLibraryOid: null, quoteOid: null, eddTemplateOid: null, eddFormat: null,
  reportTemplate: null, cocTemplate: null, tatOid: null, routeIds: [],
  balanceOid: null, sampleMatrixIds: [], sampleCategoryIds: [], testIds: [],
  testNames: null, numberOfSample: 1, comment: null, samplingAddress: null,
  siteMapId: null, batchId: null, packageNo: null, active: false, retire: false,
  retireDate: null, complianceProject: false, specialNotesHtml: null,
  daysInAdvance: 0, fileUploadOid: null,
};

/**
 * Settings → Pre-Login Settings — DETAIL view.
 *
 * Port of the legacy XAF `COCSettings_DetailView_Copy_SampleRegistration`.
 * The field set, order, captions and required markers are taken from the LEGACY
 * SCREEN (owner screenshot 2026-09-23), not from the xafml layout node:
 *
 *   General Information — left:  COC ID(ro) · COC Name · Client* · Client
 *                                Address(ro) · Client Phone(ro) · Client Contact ·
 *                                Quote ID · EDD Template · EDD Export Format ·
 *                                Report Template · COC Template · Retire Date ·
 *                                Route ID
 *                         right: Project Manager · Project ID* · Project Name(ro) ·
 *                                Project Location(ro) · Permit · Project Category ·
 *                                Project Code(ro) · #Day In Advance ·
 *                                Compliance Project ☐ · Special Notes ☐
 *   Sample Information  — Sample Matrix* · Test · No Of Samples | Sample
 *                         Category · Remark
 *   Report Requirements — TAT
 *
 * THREE fields are required (red `*`), all three carrying `[RuleRequiredField]`
 * on the BO: Client, Project ID and Sample Matrix. "Remark" is
 * `COCSettings.Comment`, which the BO captions with `[XafDisplayName("Remark")]`.
 * "Special Notes" is the non-persistent `SpecialNotesCHB` CHECKBOX ("has a note
 * been typed"); ticking it reveals the note editor.
 *
 * Fields the legacy detail does NOT show are not rendered (Email, Active,
 * Batch ID, Package No, Sampling Address, Site Map ID, Balance, Next/Last
 * Update Date). They are still round-tripped on save, so nothing is lost.
 *
 * <p>Class B (master data with child collections), so it follows the BO UI
 * Standard v4 §4 "Settings BO — Detail View" anatomy: header ({BO} – {record}
 * title · breadcrumb · prev/next arrows, grey back / green forward) · the
 * view-only Audit chip on the TITLE row, never in an action bar (§6/§8) ·
 * the action bar rendered at TOP and BOTTOM with an identical set · the
 * XAF-canonical save order Save · Save and Close · Save and New · | · Cancel,
 * all through the shared `useSaveFlow` · required-yet-empty fields on the
 * yellow `--ai-required-bg` tint with a red `*` on the label (§10) · child
 * collections as tabs, each grid owning its own section toolbar.</p>
 *
 * <p>Fields use the shared `.sr-fgrid` from `sampleRegistration.css` — which is
 * already the v4 §10 layout (label LEFT of its field on the same row,
 * left-aligned, two balanced columns, XAF-tight rows). Reusing it rather than
 * forking a second grid is standing rules 8/9, and it also answers the user's
 * "same design as Sample Registration".</p>
 *
 * <p>This is a bespoke detail rather than the shared `<SettingsDetailPage>`
 * (standing rule 12): that component is driven by a flat `MODULE_CRUD` field
 * list and cannot host the samples → tests → containers tree. The v4 anatomy it
 * would have provided is reproduced here point by point, above.</p>
 *
 * <p>Below the form, ONE tab strip carries all five child collections with the
 * legacy's own counts — Samples(n) · Tests(n) · Containers(n) · Attachment(n) ·
 * Note(n). The legacy screen splits these between three buttons and a separate
 * tabbed panel; v4 §4 asks for child collections as tabs, so they are unified.
 * NOTE "Tests" counts DISTINCT TestMethod, not test rows — the legacy button
 * reads Tests(7) where 13 rows exist, because the rows are that test's
 * parameters.</p>
 *
 * <p><b>Deferred (owner decision 2026-09-23):</b> the legacy Recurrence editor
 * (the DevExpress XAF scheduler over `dbo.Event.RecurrenceInfoXml`). The legacy
 * detail does not surface the recurrence dates either, so nothing is shown for
 * it; the stored schedule is left untouched.</p>
 */
/** Set when a child button saved a NEW record and the page is about to re-seat on
 *  it: the fresh page must start out "edited", exactly as G5's does. */
let remarkEditedOnMount = false;

/** Module Access Control: a role that may not edit this record sees it as a locked
 *  form. A disabled `<fieldset>` switches off every box, tick and button inside it in
 *  one go (the System Management `.sm-viewlock` pattern); `pointer-events: none`
 *  (preLoginSettings.css) covers the custom dropdowns that are not native controls.
 *  When the role CAN edit it renders its children untouched. */
function ViewLock({ locked, children }: { locked: boolean; children: ReactNode }) {
  return locked ? <fieldset className="pl-viewlock" disabled>{children}</fieldset> : <>{children}</>;
}

export default function PreLoginSettingsDetailPage({
  oid, siblingIds = [], onClose, onNavigate, onSaved, initialTarget,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const { confirm } = useConfirm();

  /** Bumped by Refresh to re-run the header load below. */
  const [reloadTick, setReloadTick] = useState(0);
  const [detail, setDetail]   = useState<PreLoginDetail | null>(null);
  const [form, setForm]       = useState<PreLoginInput>(BLANK);
  const [lookups, setLookups] = useState<PreLoginLookup[]>([]);
  const [loading, setLoading] = useState(true);
  /** null = the form; otherwise the collection page that REPLACES it. */
  const [childPage, setChildPage] = useState<ChildTab | null>(
    initialTarget && initialTarget !== 'schedule' ? initialTarget : null);
  /** The Task Scheduler POPUP (legacy opens it as a modal window, not a tab). */
  const [scheduleOpen, setScheduleOpen] = useState(initialTarget === 'schedule');
  /** The schedules are loaded by the FORM, not the popup: "#Days In Advance" is
   *  gated on whether one exists, and a count that only arrived once the user
   *  opened the popup would leave the field wrongly unlocked until they did.
   *  `count` starts at -1 = "not yet loaded", so the field is not disabled during
   *  the first paint — which would flicker and would wrongly lock it for a COC
   *  that does have a schedule. */
  const schedules = usePreLoginSchedules(oid);
  const scheduleCount = schedules.count;
  /** The legacy "Special Notes" CHECKBOX (SpecialNotesCHB) — a non-persistent
   *  "has a note been typed". Ticking it reveals the note editor. */
  const [specialNotesOn, setSpecialNotesOn] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  /** Set on the first save attempt. The required TINT is unconditional (v4 §10 —
   *  a required-yet-empty field is yellow from the moment the form opens), so
   *  this only drives the reset in "Save and New". */
  const [, setTriedSave] = useState(false);

  const isNew = oid === null;

  // Module Access Control: a new record needs Create, an existing one Write. Without
  // it the record opens locked and none of the Save buttons exist (hidden, not greyed).
  const perm = useNavActions('set-prelogin');
  const canEdit = isNew ? perm.create : perm.write;

  /** G5 (XAF WebConfirmUnsavedChangesDetailViewController) keeps asking "Unsaved
   *  changes will be lost" on Cancel for as long as the form was edited since it
   *  opened - even after the Sample button has already committed the record. The
   *  shared guard forgets edited controls once they leave the page, and opening a
   *  collection page removes the whole form, so we re-mark the page when it
   *  comes back (and on the freshly re-seated page after a new record's first
   *  save). A real Save clears it. */
  const reDirty = useRef(false);
  const markEdited = useCallback(() => markUserEdit(document.querySelector('.content')), []);
  useEffect(() => {
    if (remarkEditedOnMount) { remarkEditedOnMount = false; reDirty.current = true; markEdited(); }
  }, [markEdited]);

  const patch = useCallback(
    (p: Partial<PreLoginInput>) => setForm(f => ({ ...f, ...p })), []);

  // ── Load the header ───────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isNew) { setDetail(null); setForm(BLANK); setLoading(false); return; }
      setLoading(true);
      try {
        const res = await api.get(`/api/v1/pre-login-settings/${oid}`);
        if (cancelled) return;
        const d: PreLoginDetail = res.data?.data;
        setDetail(d);
        setForm({
          cocName: d.cocName, clientOid: d.clientOid, clientContactOid: d.clientContactOid,
          email: d.email, projectOid: d.projectOid, projectManagerOid: d.projectManagerOid,
          projectCategoryOid: d.projectCategoryOid, projectSource: d.projectSource,
          projectOverview: d.projectOverview, projectCity: d.projectCity,
          permitLibraryOid: d.permitLibraryOid, quoteOid: d.quoteOid,
          eddTemplateOid: d.eddTemplateOid, eddFormat: d.eddFormat,
          reportTemplate: d.reportTemplate, cocTemplate: d.cocTemplate,
          tatOid: d.tatOid, routeIds: splitIds(d.routeIds), balanceOid: d.balanceOid,
          sampleMatrixIds:   splitIds(d.sampleMatrixIds),
          sampleCategoryIds: splitIds(d.sampleCategoryIds),
          testIds:           splitIds(d.testIds),
          // G5 (COCSettingsViewController :512-514, :1361): NoOfSamples is the live count of the
          // COC's samples, and reads 1 when there are none — not the stale stored number.
          testNames: d.testNames, numberOfSample: (d.sampleCount ?? 0) > 0 ? d.sampleCount : 1,
          comment: d.comment, samplingAddress: d.samplingAddress,
          siteMapId: d.siteMapID, batchId: d.batchID, packageNo: d.packageNo,
          active: d.active, retire: d.retire,
          retireDate: d.retireDate ? d.retireDate.slice(0, 10) : null,
          complianceProject: d.complianceProject,
          specialNotesHtml: d.specialNotesHtml,
          daysInAdvance: d.daysinAdvance, fileUploadOid: d.fileUploadOid,
        });
        // SpecialNotesCHB is derived: ticked when a note exists.
        setSpecialNotesOn(!!d.specialNotesText?.trim());
      } catch {
        if (!cancelled) notify.error('Could not load this Pre-Login Setting.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [oid, isNew, notify, reloadTick]);

  // ── Load the lookups, re-fetched when the client changes ──────────────────
  // Project / contact / quote are CLIENT-SCOPED server-side and come back empty
  // without a client — legacy parity, so the cascade must re-query on change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/pre-login-settings/lookups', {
          params: form.clientOid ? { clientId: form.clientOid } : undefined,
        });
        if (!cancelled) setLookups(res.data?.data ?? []);
      } catch {
        if (!cancelled) notify.error('Could not load the dropdown lists.');
      }
    })();
    return () => { cancelled = true; };
  }, [form.clientOid, notify]);

  /** Rows of one lookup kind, in SP order. */
  const of = useCallback(
    (kind: LookupKind) => lookups.filter(l => l.kind === kind), [lookups]);

  /** `{value,label}` options for a single-select `<ComboSelect>` — the VALUE is
   *  the Oid, so a duplicate display name can never select the wrong record. */
  const options = useCallback(
    (kind: LookupKind) => of(kind).map(l => ({ value: l.id, label: l.name ?? '' })),
    [of]);

  /** Text-valued dropdown (Report / COC Template): the VALUE is the name, because
   *  those columns store the template's name, not an Oid. A saved value that is no
   *  longer in the list is kept as an option so opening a record never blanks it. */
  const nameOptions = useCallback((kind: LookupKind, current: string | null) => {
    const names = [...new Set(of(kind).map(l => l.name ?? '').filter(Boolean))];
    if (current && !names.includes(current)) names.unshift(current);
    return names.map(n => ({ value: n, label: n }));
  }, [of]);

  /** Test picker rows for the matrices ticked in Sample Information: Test ·
   *  Method · IsGroup. Legacy scopes the list to the COC's selected matrices. */
  /** Names of the ticked routes. Legacy stores the Oids upper-case and the lookup
   *  serves them lower-case, so ids are compared case-insensitively - a plain
   *  `includes` matched none of them and the box stayed empty. */
  const routeNames = useMemo(() => {
    const have = new Set(form.routeIds.map(i => i.toLowerCase()));
    return of('route').filter(r => have.has(r.id.toLowerCase())).map(r => r.name ?? '');
  }, [of, form.routeIds]);

  const testRows = useMemo(() => {
    const matrixOids = new Set(
      of('matrix').filter(m => form.sampleMatrixIds.includes(m.id))
        .map(m => (m.extra1 ?? '').toLowerCase()).filter(Boolean));
    return of('test')
      .filter(t => matrixOids.has(((t.extra2 ?? '').split('|')[0] ?? '').toLowerCase()))
      .map(t => [t.name ?? '', t.extra1 ?? '', (t.extra2 ?? '').split('|')[1] ?? 'No']);
  }, [of, form.sampleMatrixIds]);

  /** A multi-column grid picker (legacy look) plus an index→Oid resolver.
   *  Column 0 is the display name, never the Oid (no GUIDs in a grid).
   *  `LookupCombo` filters rows with `.filter()`, which preserves array
   *  identity, so `indexOf` on the picked row resolves the EXACT record even
   *  when two clients share a name. */
  const gridLookup = useCallback((kind: LookupKind, cols: string[]): {
    lookup: LookupData;
    idOf: (row: string[]) => string | null;
    nameOf: (id: string | null) => string;
  } => {
    const rows = of(kind);
    const data: LookupData = {
      cols,
      rows: rows.map(l => [l.name ?? '', l.extra1 ?? '', l.extra2 ?? ''].slice(0, cols.length)),
    };
    return {
      lookup: data,
      idOf: (row) => {
        const i = data.rows.indexOf(row);
        return rows[i]?.id ?? null;
      },
      nameOf: (id) => rows.find(r => r.id === id)?.name ?? '',
    };
  }, [of]);

  const clientPick  = useMemo(() => gridLookup('client',  ['Client', 'Address', 'Phone']), [gridLookup]);

  /** Project ID picker — legacy shows the project's own ID in the box, with the
   *  name and code as extra grid columns. Rows are [ID, Name, Code] in that order;
   *  the Oid comes back through the same index trick as every grid lookup. */
  const projectPick = useMemo(() => {
    const rows = of('project');
    const data: LookupData = {
      cols: ['Project ID', 'Project Name', 'Project Code'],
      rows: rows.map(r => [r.extra1 ?? '', r.name ?? '', r.extra2 ?? '']),
    };
    return {
      lookup: data,
      idOf: (row: string[]) => rows[data.rows.indexOf(row)]?.id ?? null,
      nameOf: (id: string | null) => rows.find(r => r.id === id)?.extra1 ?? '',
    };
  }, [of]);

  /** Permit picker — the legacy grid is ID | PermitType | Name | Number and the
   *  box shows the PermitType. */
  const permitPick = useMemo(() => {
    const rows = of('permit');
    const detail = new Map(of('permitDetail').map(d => [d.id.toLowerCase(), d]));
    const data: LookupData = {
      cols: ['ID', 'PermitType', 'Name', 'Number'],
      rows: rows.map(r => {
        const d = detail.get(r.id.toLowerCase());
        return [d?.extra1 ?? '', r.name ?? '', d?.name ?? '', r.extra1 ?? ''];
      }),
    };
    return {
      lookup: data,
      idOf: (row: string[]) => rows[data.rows.indexOf(row)]?.id ?? null,
      nameOf: (id: string | null) => rows.find(r => r.id === id)?.name ?? '',
    };
  }, [of]);

  /** Read-only fields that follow the pick the moment it is made (legacy fills them
   *  on selection, not on save). Falls back to what the server returned for a saved
   *  record whose pick has not changed or whose lookup row is not loaded yet. */
  const pickedClient  = of('client').find(c => c.id === form.clientOid);
  const pickedProject = of('project').find(p => p.id === form.projectOid);
  const projectLocation = of('projectLocation').find(l => l.id === form.projectOid)?.name;
  const live = {
    clientAddress:   pickedClient ? (pickedClient.extra1 ?? '') : (detail?.clientAddress ?? ''),
    clientPhone:     pickedClient ? (pickedClient.extra2 ?? '') : (detail?.clientPhone ?? ''),
    projectName:     pickedProject ? (pickedProject.name ?? '') : (form.projectOid ? (detail?.projectName ?? '') : ''),
    projectLocation: pickedProject ? (projectLocation ?? '') : (form.projectOid ? (detail?.projectLocation ?? '') : ''),
    projectCode:     pickedProject ? (pickedProject.extra2 ?? '') : (form.projectOid ? (detail?.projectCode ?? '') : ''),
  };

  /** Names for the two multi-selects, derived from the picked ids. */
  const matrixNames = useMemo(
    () => of('matrix').filter(m => form.sampleMatrixIds.includes(m.id)).map(m => m.name ?? ''),
    [of, form.sampleMatrixIds]);
  const categoryNames = useMemo(
    () => of('sampleCategory').filter(c => form.sampleCategoryIds.includes(c.id)).map(c => c.name ?? ''),
    [of, form.sampleCategoryIds]);

  // ── Save ──────────────────────────────────────────────────────────────────
  // ONE handler behind all three saves; only the after-step differs (BO UI
  // Standard v4 §4: "All three saves call the shared useSaveFlow — same confirm
  // → handler → 'Saved successfully.' — with a different onSaved per button").
  /** When a child button is pressed on an UNSAVED record we save first and then
   *  open it. The page is keyed on the oid, so the save remounts it — the intent
   *  has to travel through the parent (`onNavigate`'s second argument) rather
   *  than living in this component's state, which does not survive. */
  const doSave = useCallback(async (after: 'stay' | 'close' | 'new', openAfter?: PostSaveTarget) => {
    setTriedSave(true);
    // "#Day In Advance" is typed freely (G5 parity) but only means something once
    // the COC has a task schedule, so the SERVER forces it back to 0 and says why.
    // Tell the user, and put the 0 on screen, so the field does not just snap back.
    const noteDaysInAdvanceReset = (res: { data?: { daysInAdvanceCleared?: boolean; warning?: string } }) => {
      if (!res.data?.daysInAdvanceCleared) return;
      patch({ daysInAdvance: 0 });
      notify.warning(res.data.warning ?? 'RecurranceSetup is not available.');
    };

    // An emptied "#Day In Advance" is held as null so the box can STAY empty while
    // typing; it is written as 0, because dbo.COCSettings.DaysinAdvance has no NULL
    // in any of its 355 live rows and G5 commits 0 for a blank box. Saving null here
    // would put the first NULL into that column.
    const payload = { ...form, daysInAdvance: form.daysInAdvance ?? 0 };

    // Mirrors the server's authoritative check — the THREE legacy
    // [RuleRequiredField] markers on this BO, which the legacy detail draws
    // with a red * : Client, Project ID and Sample Matrix.
    if (!form.clientOid)                    { notify.error("'Client' must not be empty."); return; }
    if (!form.projectOid)                   { notify.error("'Project ID' must not be empty."); return; }
    if (form.sampleMatrixIds.length === 0)  { notify.error("'Sample Matrix' must not be empty."); return; }

    // A child button (Samples / Tests / Containers / Parameters / Task Scheduler)
    // on an UNSAVED record: legacy's Sample_Execute commits the record with no
    // "Do you want to Save?" and no "Saved" box, then opens the window. Match it -
    // the user asked to open the samples, not to save, so a confirm + success
    // dialog in between is two interruptions for one click. Create is audit-exempt
    // and reason-exempt, so skipping the shared dialog loses nothing.
    if (isNew && openAfter) {
      try {
        const res = await runLoading(
          api.post('/api/v1/pre-login-settings', payload),
          { blocking: true, label: 'Saving…' });
        noteDaysInAdvanceReset(res);
        const created = res.data?.data;
        // Saved, but NOT clean: G5 still treats the form as edited (see reDirty).
        remarkEditedOnMount = true;
        onSaved?.();
        if (created?.oid) onNavigate?.(created.oid, openAfter);
      } catch (e) {
        notify.error(serverMessage(e) ?? 'Could not save the record.');
      }
      return;
    }

    await runSave({
      formName: PRE_LOGIN_FORM,
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        if (isNew) {
          const res = await api.post('/api/v1/pre-login-settings', payload);
          noteDaysInAdvanceReset(res);
          const created = res.data?.data;
          // Re-seat the page on the saved record so the minted COC id shows and
          // the Samples tab has a parent to attach to — unless we are clearing
          // for the next entry, where staying on the new record would defeat it.
          if (created?.oid && after !== 'new') onNavigate?.(created.oid, openAfter);
        } else {
          const res = await api.put(`/api/v1/pre-login-settings/${oid}`, payload);
          noteDaysInAdvanceReset(res);
          if (openAfter === 'schedule') setScheduleOpen(true);
          else if (openAfter) setChildPage(openAfter);
        }
      },
      onSaved: () => {
        reDirty.current = false;
        onSaved?.();
        if (after === 'close') onClose();
        if (after === 'new') {
          // Clear for the next entry (v4 "Save and New").
          setDetail(null);
          setForm(BLANK);
          setTriedSave(false);
          setChildPage(null);
        }
      },
    });
  }, [form, isNew, oid, runSave, notify, onSaved, onClose, onNavigate]);

  /** Re-reads the header's server-computed values (sample / test / container counts,
   *  and what the "No Of Samples" and Test fields lock on) WITHOUT touching the form,
   *  so edits not yet saved are kept. Called when the user returns from a collection
   *  page, where those counts will have changed. */
  const refreshDetail = useCallback(async () => {
    if (!oid) return;
    try {
      const res = await api.get(`/api/v1/pre-login-settings/${oid}`);
      const d: PreLoginDetail | undefined = res.data?.data;
      if (d) {
        setDetail(d);
        // Back from the Samples page (G5's popup-close handler): the count, or 1 when none.
        patch({ numberOfSample: (d.sampleCount ?? 0) > 0 ? d.sampleCount : 1 });
      }
    } catch { /* the counts just stay as they were; nothing to tell the user */ }
  }, [oid, patch]);

  /** Refresh — G5's Refresh reloads the record from the database. Edits not yet
   *  saved are thrown away, so the shared unsaved-changes confirm (standing rule 15)
   *  is asked first. */
  const doRefresh = useCallback(async () => {
    if (!oid) return;
    if (hasUnsavedChanges()) {
      const ok = await confirm({
        title: 'Refresh',
        message: 'Unsaved changes will be lost. Do you want to proceed?',
        confirmLabel: 'OK',
      });
      if (!ok) return;
    }
    resetUnsavedTracking();
    setReloadTick(t => t + 1);
    schedules.reload();
  }, [oid, confirm, schedules]);

  /** Save As — legacy COCSaveAs_Execute, which targets this detail view as well as
   *  the list: clones the record (header, samples, tests, containers, attachments,
   *  notes) under a new COC id and opens the clone. */
  const doSaveAs = useCallback(async () => {
    if (!oid) return;
    const ok = await confirm({
      title: 'Save As',
      message: `Create a copy of ${detail?.cocId ?? 'this record'}? `
             + 'The copy takes its samples, tests, containers, attachments and '
             + 'notes — you fill in the client and project on the copy.',
      confirmLabel: 'Save As',
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/pre-login-settings/${oid}/save-as`);
      const clone = res.data?.data as { oid: string } | undefined;
      notify.success('Saved successfully.');
      onSaved?.();
      if (clone?.oid) onNavigate?.(clone.oid);   // legacy opens the clone
    } catch (err) {
      notify.error(serverMessage(err) || 'Could not copy this Pre-Login Setting.');
    }
  }, [oid, detail, confirm, notify, onSaved, onNavigate]);

  /** Delete — the same audited, reason-gated delete the list uses. A COC already
   *  imported into a job is refused by the server (409), whose message the shared
   *  flow shows verbatim. Returns to the list afterwards. */
  const doDelete = useCallback(async () => {
    if (!oid) return;
    await runDelete({
      formName: PRE_LOGIN_FORM,
      message: `Delete ${detail?.cocId ?? 'this record'}? Its samples, tests and bottle allocations are removed with it.`,
      delete: async () => { await api.delete(`/api/v1/pre-login-settings/${oid}`); },
      onDeleted: () => { resetUnsavedTracking(); onSaved?.(); onClose(); },
    });
  }, [oid, detail, runDelete, onSaved, onClose]);

  /** Open one of the child collections. They hang off a SAVED record, so on an
   *  unsaved one this saves first and opens it after — the buttons stay usable
   *  rather than being greyed until the user works out that Save unlocks them
   *  (owner request 2026-09-28: "the buttons ... should be always available").
   *  A save that fails validation reports the same message Save itself would,
   *  and nothing opens. */
  const openChild = useCallback((id: ChildTab) => {
    if (oid) { reDirty.current = reDirty.current || hasUnsavedChanges(); setChildPage(id); return; }
    void doSave('stay', id);
  }, [oid, doSave]);

  // ── Prev / next record ────────────────────────────────────────────────────
  const idx  = oid ? siblingIds.indexOf(oid) : -1;
  const prev = idx > 0 ? siblingIds[idx - 1] : null;
  const next = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : null;

  // ── Keyboard (standing rule 14) ───────────────────────────────────────────
  // This detail has a hand-rolled action row rather than <AppToolbar>, so its
  // buttons are registered with the ONE shared hotkey layer — never a raw
  // document listener. Keys are the reserved defaults for these verbs.
  // The Save shortcuts exist only for a role that may edit (Module Access Control).
  useHotkeys([
    ...(canEdit ? [
      { combo: 'mod+s',       label: 'Save',           group: 'Pre-Login Settings', order: 1,
        run: () => void doSave('stay') },
      { combo: 'mod+enter',   label: 'Save and Close', group: 'Pre-Login Settings', order: 2,
        run: () => void doSave('close') },
    ] : []),
    // Save and New saves, then clears for a NEW record, so it needs Create as well.
    ...(canEdit && perm.create ? [
      { combo: 'mod+shift+s', label: 'Save and New',   group: 'Pre-Login Settings', order: 3,
        run: () => void doSave('new') },
    ] : []),
    { combo: 'esc',         label: 'Cancel',         group: 'Pre-Login Settings', order: 4,
      run: () => onClose() },
    // Refresh / Delete exist on a saved record only (the buttons mirror these).
    ...(oid ? [
      { combo: 'alt+r', label: 'Refresh', group: 'Pre-Login Settings', order: 7,
        run: () => void doRefresh() },
    ] : []),
    ...(oid && perm.delete ? [
      { combo: 'alt+d', label: 'Delete', group: 'Pre-Login Settings', order: 8,
        run: () => void doDelete() },
    ] : []),
    { combo: 'alt+left',    label: 'Previous record', group: 'Pre-Login Settings', order: 5,
      disabled: !prev, run: () => prev && onNavigate?.(prev) },
    { combo: 'alt+right',   label: 'Next record',     group: 'Pre-Login Settings', order: 6,
      disabled: !next, run: () => next && onNavigate?.(next) },
  ]);

  /** Required-yet-empty → the shared yellow tint (BO UI Standard v4 §4/§10 —
   *  `--ai-required-bg` #fffbe7). The red `*` comes from `.sr-fgrid > label.req`,
   *  so the label and the field are marked by the same one rule. */
  const reqCls = (empty: boolean) => (empty ? 'pl-req-empty' : undefined);

  /** Child samples this COC already has — legacy's live NoOfSamples. */
  const sampleCount = detail?.sampleCount ?? 0;

  /** The action bar — rendered at the TOP and the BOTTOM of the form with an
   *  IDENTICAL set (v4 §4 "Action bar renders at top and bottom of the form
   *  (legacy parity), identical set both ends"). Order and wording are the
   *  XAF-canonical ones: Save · Save and Close · Save and New · | · Cancel —
   *  "and" spelled out, a thin divider before Cancel, Cancel's undo glyph blue. */
  /** TOP and BOTTOM now carry different navigation buttons (owner request
   *  2026-09-29): the top bar has Sample Registration and Task Scheduler, the
   *  bottom bar has Samples / Tests / Containers / Parameters. The Save group is
   *  identical at both ends. */
  const renderActionBar = (where: 'top' | 'bottom') => (
    <div className="sr-savebar">
      {where === 'top' && (<>
      {/* Sample Registration — FIRST, and always available (owner request
          2026-09-28). This is the one button here that leaves the form entirely:
          a Pre-Login Setting is the TEMPLATE a registration imports, so the
          natural next step from the template is the jobs built from it.

          It opens the LIST view (standing rule 10) — `navigateToPage` with no
          record id. Never a bare `navigate()`: only this helper also moves
          AppShell's own `page` state, expands the sidebar group so the nav
          highlights where the user now is, and runs the unsaved-changes guard
          (standing rule 15), which matters because this can be pressed with the
          form half-filled. Unlike the child buttons beside it, it does NOT save
          first — it is not opening a collection of THIS record. */}
      <button className="ca-btn" onClick={() => navigateToPage('sampreg')}
              title="Open the Sample Registration list">
        <i className="ti ti-clipboard-list" /> Sample Registration
      </button>
      </>)}
      {where === 'bottom' && (<>
      {/* The legacy detail's bottom buttons — Samples(n) · Tests(n) ·
          Containers(n) — plus Parameters, which opens the same page on its own
          tab (Sample Registration puts Parameters next to Containers the same
          way). They OPEN A SEPARATE PAGE rather than a tab, matching both the
          legacy screen and Sample Registration.

          They are ALWAYS ENABLED, including on New (owner request 2026-09-28:
          "the buttons ... should be always available"). They hang off a saved
          record, so pressing one on an unsaved record saves first and then opens
          it — see `openChild`. They were briefly greyed on New; a disabled
          button that gives no way to proceed is worse than one that does the
          obvious thing. The counts read 0 until there is something to count.

          Parameters carries NO count here: its rows are per-SAMPLE, and the
          header's counts are per-COC. A number next to it would be read as
          "this COC has N parameters", which is not what it would mean. */}
      {(['samples', 'tests', 'containers', 'parameters'] as const).map(id => (
        <button key={id} className="ca-btn"
                title={oid ? undefined : 'Saves this Pre-Login Setting first, then opens it.'}
                onClick={() => openChild(id)}>
          <i className={id === 'samples'    ? 'ti ti-flask'
                      : id === 'tests'      ? 'ti ti-microscope'
                      : id === 'containers' ? 'ti ti-bottle' : 'ti ti-list-details'} />
          {' '}{id === 'samples'    ? `Samples (${sampleCount})`
              : id === 'tests'      ? `Tests (${detail?.testCount ?? 0})`
              : id === 'containers' ? `Containers (${detail?.containerCount ?? 0})`
              :                       'Parameters'}
        </button>
      ))}
      </>)}
      {/* Task Scheduler — NEXT TO Parameters, and a POPUP, exactly as legacy
          does it: the "Task Scheduler" action is targeted at this detail view
          and TaskScheduler_Execute shows TaskRecurranceSetup_ListView_COCSettings
          with TemplateContext.PopupWindow + TargetWindow.NewModalWindow. It is
          the only one of these buttons that does NOT replace the page, because
          it is a lookup at the COC's schedule rather than one of its
          collections. Always enabled like the others; on an unsaved record it
          saves first, so the popup opens against a real COC. */}
      {where === 'top' && (
      <button className="ca-btn"
              title={oid ? undefined : 'Saves this Pre-Login Setting first, then opens it.'}
              onClick={() => { if (oid) setScheduleOpen(true); else void doSave('stay', 'schedule'); }}>
        <i className="ti ti-calendar-repeat" />
        {' '}Task Scheduler{scheduleCount > 0 ? ` (${scheduleCount})` : ''}
      </button>
      )}
      {/* Reports ▾ — the lab-assigned templates for the OPEN record (Form Report
          Permission, view COCSettings_DetailView_Copy_SampleRegistration), as in
          G5. Needs a saved COC (nothing to report on a new one); hidden when no
          report is assigned. */}
      {where === 'top' && detail?.cocId && (
      <FormReportsMenu
        reportsUrl="/api/v1/pre-login-settings/reports?view=detail"
        pdfUrl="/api/v1/pre-login-settings/report-pdf"
        keysParam="cocIds"
        selectedKeys={[detail.cocId]} />
      )}
      {/* Delete · Refresh · Save As — G5's detail toolbar (Reports · Delete · Refresh ·
          Save As · Task Scheduler · Audit). Saved records only; Delete and Save As follow
          the role's Delete / Create rights, as on the list. */}
      {where === 'top' && oid && (<>
      {perm.delete && (
      <button className="ca-btn" onClick={() => void doDelete()}
              title={`Delete (${formatCombo('alt+d')})`}>
        <i className="ti ti-trash" style={{ color: 'var(--ai-red, #d32f2f)' }} /> Delete
      </button>
      )}
      <button className="ca-btn" onClick={() => void doRefresh()}
              title={`Refresh (${formatCombo('alt+r')})`}>
        <i className="ti ti-refresh" /> Refresh
      </button>
      {perm.create && (
      <button className="ca-btn" onClick={() => void doSaveAs()} title="Save As">
        <i className="ti ti-copy" /> Save As
      </button>
      )}
      </>)}
      <span className="sr-savebar-spacer" />
      {canEdit && (<>
      <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')}
              title={`Save (${formatCombo('mod+s')})`}>
        <i className="ti ti-device-floppy" /> Save
      </button>
      <button className="ca-btn" onClick={() => void doSave('close')}
              title={`Save and Close (${formatCombo('mod+enter')})`}>
        <i className="ti ti-device-floppy" /> Save and Close
      </button>
      {perm.create && (
      <button className="ca-btn" onClick={() => void doSave('new')}
              title={`Save and New (${formatCombo('mod+shift+s')})`}>
        <i className="ti ti-file-plus" /> Save and New
      </button>
      )}
      <span className="sr-sdiv" />
      </>)}
      <button className="ca-btn" onClick={onClose} title={`Cancel (${formatCombo('esc')})`}>
        <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-blue)' }} /> {canEdit ? 'Cancel' : 'Close'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="bl-page sr-detail pl-detail" style={{ display: 'grid', placeItems: 'center' }}>
        <LoaderSymbol size={32} />
      </div>
    );
  }

  // ── The collection page REPLACES the form ─────────────────────────────────
  // Same shape as Sample Registration: step 0 is the form, and Sample Login /
  // Tests / Containers swap it out for their own page rather than nesting.
  // After every hook above, so hook order stays stable (rules of hooks).
  if (childPage && oid) {
    return (
      <PreLoginSamplesPage
        cocOid={oid}
        cocId={detail?.cocId ?? null}
        tab={childPage}
        setTab={setChildPage}
        testCount={detail?.testCount ?? 0}
        containerCount={detail?.containerCount ?? 0}
        readOnly={!canEdit}
        onBack={() => { setChildPage(null); void refreshDetail(); if (reDirty.current) markEdited(); }}
      />
    );
  }

  return (
    <div className="bl-page sr-detail pl-detail">
      {/* ── Header: breadcrumb + record title + prev/next ──────────────────── */}
      <div className="bl-top">
        <div className="sr-crumb">
          <a onClick={onClose}>Settings</a>
          <span className="sr-crumb-sep">›</span>
          <a onClick={onClose}>Pre-Login Settings</a>
          <span className="sr-crumb-sep">›</span>
          <b>{isNew ? 'New' : detail?.cocId ?? ''}</b>
        </div>

        {/* Title row: "{BO} – {record}" + prev/next arrows + the VIEW-ONLY audit
            chip. The chip lives here, never in an action bar (v4 §6/§8 ruling
            2026-07-01), and §8 requires it on List, Detail AND New alike. */}
        <div className="sr-savebar">
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--ai-ink)' }}>
            Pre-Login Settings {detail?.cocId ? `– ${detail.cocId}` : ''}
            {detail?.cocName ? <span style={{ color: 'var(--ai-muted)', fontWeight: 400 }}> · {detail.cocName}</span> : null}
          </h2>
          <FormTitle pageId="set-prelogin" auditFormName={PRE_LOGIN_FORM}
                     auditSourceId={oid ?? undefined} />
          <span className="sr-savebar-spacer" />
          {/* v4 §4: grey back, green forward. */}
          <button className="ca-btn" disabled={!prev}
                  title={`Previous record (${formatCombo('alt+left')})`}
                  onClick={() => prev && onNavigate?.(prev)}>
            <i className="ti ti-chevron-left" style={{ color: 'var(--ai-muted)' }} />
          </button>
          <button className="ca-btn" disabled={!next}
                  title={`Next record (${formatCombo('alt+right')})`}
                  onClick={() => next && onNavigate?.(next)}>
            <i className="ti ti-chevron-right" style={{ color: 'var(--ai-green)' }} />
          </button>
        </div>

        {/* Action bar — TOP (the identical set renders again at the bottom). */}
        {renderActionBar('top')}
      </div>

      <div className="bl-grid-area" style={{ overflow: 'auto' }}>
        <ViewLock locked={!canEdit}>
        {/* ── General Information ─────────────────────────────────────────
            Field order, labels and required markers are taken from the LEGACY
            XAF detail (owner screenshot 2026-09-23). `.sr-fgrid` is a 4-column
            grid — label·field·label·field — so the pairs below read down the
            LEFT column and then the RIGHT one exactly as the legacy form does.
            A `<span />` is an intentional empty slot where the legacy right
            column runs out before the left one. ── */}
        <section className="sr-sec">
          <h3 className="sr-sec-h"><span className="sr-sec-ico">📋</span> General Information</h3>
          <div className="sr-sec-b">
            {/* TWO COMPACT COLUMNS (owner request 2026-09-29: two columns like the
                legacy form, but short). Each column is its own label·field grid, so
                it is only as tall as its own fields: Client & contact | Project.
                Field set, labels and required markers unchanged. */}
            <div className="pl-cols2">
              <div className="sr-fgrid">
                <label>COC ID</label>
                <div><input type="text" readOnly value={detail?.cocId ?? ''} placeholder="Autogenerate" /></div>

                <label>COC Name</label>
                <div>
                  <input type="text" value={form.cocName ?? ''}
                         onChange={e => patch({ cocName: e.target.value })} />
                </div>

                <label className="req">Client</label>
                <div>
                  <LookupCombo
                    lookup={clientPick.lookup}
                    value={clientPick.nameOf(form.clientOid)}
                    className={reqCls(!form.clientOid)}
                    onPick={row => {
                      // Changing client invalidates every client-scoped pick.
                      patch({
                        clientOid: clientPick.idOf(row),
                        projectOid: null, clientContactOid: null, quoteOid: null,
                      });
                    }}
                  />
                </div>

                {/* Client Address / Phone are non-persistent legacy getters
                    (Customer.Address / Customer.OfficePhone). */}
                <label>Client Address</label>
                <div><input type="text" readOnly value={live.clientAddress} /></div>

                <label>Client Phone</label>
                <div><input type="text" readOnly value={live.clientPhone} /></div>

                <label>Client Contact</label>
                <div>
                  <ComboSelect value={form.clientContactOid ?? ''} options={options('contact')}
                               onChange={v => patch({ clientContactOid: v || null })}
                               placeholder={form.clientOid ? 'Select…' : 'Pick a client first'}
                               disabled={!form.clientOid} />
                </div>

                <label>Quote ID</label>
                <div>
                  <ComboSelect value={form.quoteOid ?? ''} options={options('quote')}
                               onChange={v => patch({ quoteOid: v || null })}
                               placeholder={form.clientOid ? 'Select…' : 'Pick a client first'}
                               disabled={!form.clientOid} />
                </div>

                <label>Route ID</label>
                <div>
                  {/* Legacy: a checked (multi) grid lookup over Routes - RouteID + Route
                      columns, stored as a ";"-joined Oid list. Keyed by route NAME so the
                      box reads like legacy's strRouteID (names joined). */}
                  <LookupCombo
                    multi
                    multiActions
                    lookup={{ cols: ['RouteID', 'Route'], rows: of('route').map(r => [r.extra1 ?? '', r.name ?? '']) }}
                    rowKey={r => r[1] ?? ''}
                    values={routeNames}
                    onToggle={names => patch({
                      routeIds: of('route').filter(r => names.includes(r.name ?? '')).map(r => r.id),
                    })}
                    placeholder="Select…"
                    popupWidth={420}
                    columnFilters
                  />
                </div>

                <label>Retire Date</label>
                <div>
                  <input type="date" value={form.retireDate ?? ''}
                         onChange={e => patch({ retireDate: e.target.value || null })} />
                </div>

                <label>Compliance Project</label>
                <div>
                  <input type="checkbox" checked={form.complianceProject}
                         onChange={e => patch({ complianceProject: e.target.checked })} />
                </div>
              </div>

              <div className="sr-fgrid">
                <label className="req">Project ID</label>
                <div>
                  <LookupCombo
                    lookup={projectPick.lookup}
                    value={projectPick.nameOf(form.projectOid)}
                    className={reqCls(!form.projectOid)}
                    onPick={row => patch({ projectOid: projectPick.idOf(row) })}
                    blockedReason={form.clientOid ? undefined : 'Select a Client first — the project list is client-scoped.'}
                    onBlocked={r => notify.warning(r)}
                  />
                </div>

                {/* Project Name / Location are non-persistent legacy getters. */}
                <label>Project Name</label>
                <div><input type="text" readOnly value={live.projectName} /></div>

                <label>Project Location</label>
                <div><input type="text" readOnly value={live.projectLocation} /></div>

                <label>Project Manager</label>
                <div>
                  <ComboSelect value={form.projectManagerOid ?? ''} options={options('projectManager')}
                               onChange={v => patch({ projectManagerOid: v || null })} />
                </div>

                <label>Permit</label>
                <div>
                  <LookupCombo
                    lookup={permitPick.lookup}
                    value={permitPick.nameOf(form.permitLibraryOid)}
                    onPick={row => patch({ permitLibraryOid: permitPick.idOf(row) })}
                    placeholder="Select…"
                  />
                </div>

                <label>Project Category</label>
                <div>
                  <ComboSelect value={form.projectCategoryOid ?? ''} options={options('projectCategory')}
                               onChange={v => patch({ projectCategoryOid: v || null })} />
                </div>

                <label>Project Code</label>
                <div><input type="text" readOnly value={live.projectCode} /></div>

                <label>#Day In Advance</label>
                <div>
                  {/* This is a lead time before the next scheduled occurrence, so it
                      only means anything once the COC has a task schedule. G5 still
                      lets it be TYPED and enforces the rule on save — forcing 0 and
                      reporting "RecurranceSetup is not available."
                      (COCSettingsController.Grid_BatchUpdate :1146). We did disable the
                      field instead, which read as a broken control (owner report
                      2026-11-04: "not editable in our project but g5 it will work");
                      it is editable again, and the server's reset is surfaced on save.
                      The API stays the authoritative gate (§14b.5). */}
                  <input type="number" min={0} value={form.daysInAdvance ?? ''}
                         title={!isNew && scheduleCount === 0
                           ? 'Needs a task schedule — without one this is reset to 0 on save.'
                           : undefined}
                         onChange={e => {
                           // The box must be CLEARABLE (owner report 2026-11-04: "not
                           // able to remove 0 in our project but g5 0 remove"). Coercing
                           // through Number() turned an empty box straight back into 0,
                           // so the digit could never be deleted. Empty is held as null
                           // and shown blank; the SAVE sends 0, since no live COCSettings
                           // row has a NULL here (278 of 355 are 0) and G5 stores 0 too.
                           const v = e.target.value;
                           patch({ daysInAdvance: v === '' ? null : Number(v) });
                         }} />
                </div>

                {/* The legacy "Special Notes" is a CHECKBOX (SpecialNotesCHB, a
                    non-persistent "has a note been typed"). Ticking it reveals the
                    note editor below the grid; clearing it clears the note, which
                    is what the derived flag means. */}
                <label>Special Notes</label>
                <div>
                  {/* Legacy parity: a click never toggles the box itself - it opens the
                      Special Notes popup, and the box then reflects whether a note exists. */}
                  <input type="checkbox" checked={specialNotesOn} title="Open the special notes"
                         onChange={() => setNotesOpen(true)} />
                </div>
              </div>
            </div>

            {notesOpen && (
              <SpecialNotesDialog
                value={form.specialNotesHtml ?? ''}
                onClose={() => setNotesOpen(false)}
                onOk={html => {
                  const hasText = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim() !== '';
                  patch({ specialNotesHtml: hasText ? html : null });
                  setSpecialNotesOn(hasText);
                  setNotesOpen(false);
                }} />
            )}
          </div>
        </section>

        {/* ── Sample Information ──────────────────────────────────────────── */}
        <section className="sr-sec">
          <h3 className="sr-sec-h"><span className="sr-sec-ico">🧪</span> Sample Information</h3>
          <div className="sr-sec-b">
            <div className="pl-cols2">
              <div className="sr-fgrid">
                <label className="req">Sample Matrix</label>
                <div className={reqCls(form.sampleMatrixIds.length === 0)}>
                  <CheckedCombo
                    options={of('matrix').map(m => m.name ?? '')}
                    values={matrixNames}
                    required={form.sampleMatrixIds.length === 0}
                    onChange={names => patch({
                      sampleMatrixIds: of('matrix').filter(m => names.includes(m.name ?? '')).map(m => m.id),
                    })}
                  />
                </div>

                {/* Legacy: a multi-select GRID lookup (Test / Method / IsGroup) over
                    the tests of the selected matrices; stored as NPTest, ";"-joined
                    "Test|Method". Locked once the COC has samples (legacy
                    COCSettingsContextMentViewController), because the samples' tests
                    were built from it. */}
                <label>Test</label>
                <div>
                  <LookupCombo
                    multi
                    multiActions
                    lookup={{ cols: ['Test', 'Method', 'IsGroup'], rows: testRows }}
                    // G5 stores a plain test as "Test|Method" and a GROUP test as just its
                    // name, with no "|" (COCSettingsViewController.InsertSamplesInCOCSettingsSample:
                    // Split('|').Count == 2 -> test, == 1 -> group). A group's method is blank,
                    // so its key is the bare name; a trailing "|" written by an earlier build
                    // is dropped on read so old values still tick.
                    rowKey={r => {
                      const name = (r[0] ?? '').trim(), method = (r[1] ?? '').trim();
                      return method ? `${name}|${method}` : name;
                    }}
                    values={(form.testNames ?? '').split(';').map(x => x.trim().replace(/|$/, '')).filter(Boolean)}
                    onToggle={next => patch({ testNames: next.length ? next.join(';') : null })}
                    // Samples exist ⇒ the list still OPENS (G5: you can see what is ticked)
                    // but nothing can be changed — row clicks cancelled, ticks disabled.
                    selectionLocked={sampleCount > 0}
                    placeholder={form.sampleMatrixIds.length ? 'Select…' : 'Pick a sample matrix first'}
                    popupWidth={560}
                    columnFilters
                  />
                </div>

                {/* Legacy NoOfSamples (COCSettings.cs :843-873) is [NonPersistent]: a LIVE
                    COUNT of the child samples, not the stored NumberOfSample column. So
                    once this COC has samples the count is what XAF shows and the field is
                    read-only; while it has none the typed number is what seeds them on the
                    first open of Samples (AI_PreLoginSettings_InitSamples_SP). Binding the
                    stored column alone showed 0 on a COC with 15 samples. */}
                <label>No Of Samples</label>
                <div>
                  <input type="number" min={0}
                         value={sampleCount > 0 ? sampleCount : (form.numberOfSample ?? '')}
                         readOnly={sampleCount > 0}
                         title={sampleCount > 0
                           ? 'The number of samples on this record — add or remove them on the Samples page.'
                           : undefined}
                         onChange={e => patch({ numberOfSample: e.target.value === '' ? null : Number(e.target.value) })} />
                </div>
              </div>

              <div className="sr-fgrid">
                <label>Sample Category</label>
                <div>
                  <CheckedCombo
                    options={of('sampleCategory').map(c => c.name ?? '')}
                    values={categoryNames}
                    onChange={names => patch({
                      sampleCategoryIds: of('sampleCategory').filter(c => names.includes(c.name ?? '')).map(c => c.id),
                    })}
                  />
                </div>

                {/* Remark is COCSettings.Comment — the BO carries
                    [XafDisplayName("Remark")], so the legacy caption wins. */}
                <label>Remark</label>
                <div>
                  <textarea rows={2} value={form.comment ?? ''}
                            onChange={e => patch({ comment: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Report Requirements ─────────────────────────────────────────── */}
        <section className="sr-sec">
          <h3 className="sr-sec-h"><span className="sr-sec-ico">📄</span> Report Requirements</h3>
          <div className="sr-sec-b">
            {/* The EDD / report / COC template fields belong here, not in General
                Information (owner request 2026-09-29). */}
            <div className="pl-cols2">
              <div className="sr-fgrid">
                <label>TAT</label>
                <div>
                  <ComboSelect value={form.tatOid ?? ''} options={options('tat')}
                               onChange={v => patch({ tatOid: v || null })} />
                </div>

                <label>EDD Template</label>
                <div>
                  <ComboSelect value={form.eddTemplateOid ?? ''} options={options('eddTemplate')}
                               onChange={v => patch({ eddTemplateOid: v || null })} />
                </div>

                <label>EDD Export Format</label>
                <div>
                  <ComboSelect value={form.eddFormat == null ? '' : String(form.eddFormat)}
                               options={EDD_FORMATS.map((label, i) => ({ value: String(i), label }))}
                               onChange={v => patch({ eddFormat: v === '' ? null : Number(v) })} />
                </div>
              </div>

              <div className="sr-fgrid">
                <label>Report Template</label>
                <div>
                  <ComboSelect value={form.reportTemplate ?? ''}
                               options={nameOptions('reportTemplate', form.reportTemplate)}
                               onChange={v => patch({ reportTemplate: v || null })} />
                </div>

                <label>COC Template</label>
                <div>
                  <ComboSelect value={form.cocTemplate ?? ''}
                               options={nameOptions('cocTemplate', form.cocTemplate)}
                               onChange={v => patch({ cocTemplate: v || null })} />
                </div>
              </div>
            </div>
          </div>
        </section>
        </ViewLock>

        {/* ── Attachment | Note ────────────────────────────────────────────
            The legacy detail puts these two at the BOTTOM OF THE FORM as a
            tabbed control, so they stay here. Samples / Tests / Containers are
            NOT tabs: their buttons in the action bar open a SEPARATE PAGE, the
            same split Sample Registration uses for its Sample Login / Tests /
            Containers steps.

            Rendered on New too, with empty grids — same reason as the action-bar
            buttons above (owner request 2026-09-28): the form should not grow a
            whole new section the first time it is saved. The panel skips its
            fetch while cocOid is null. ── */}
        <PreLoginDocsPanel
          cocOid={oid}
          attachmentCount={detail?.attachmentCount ?? 0}
          noteCount={detail?.noteCount ?? 0}
          readOnly={!canEdit}
          hideWriteActions
        />

        {/* ── Task Scheduler ───────────────────────────────────────────────
            The recurring schedule(s) behind this COC (legacy
            dbo.TaskRecurranceSetup) open as a MODAL POPUP from the action-bar
            button beside Parameters — never as a panel or tab on the form.
            That is how legacy shows it (TaskRecurranceSetupController
            .TaskScheduler_Execute → TemplateContext.PopupWindow +
            TargetWindow.NewModalWindow); corrected on the owner's report
            2026-09-28, having first been built inline here. ── */}
        {!isNew && oid && (
          <PreLoginScheduleDialog
            open={scheduleOpen}
            onClose={() => setScheduleOpen(false)}
            cocOid={oid}
            info={{
              cocId: detail?.cocId ?? null,
              clientName: detail?.clientNameText ?? null,
              clientAddress: detail?.clientAddress ?? null,
              projectCode: detail?.projectCode ?? null,
              projectName: detail?.projectName ?? null,
            }}
            rows={schedules.rows}
            loading={schedules.loading}
            onRefresh={() => void schedules.reload()}
            readOnly={!canEdit}
          />
        )}

        {/* Action bar — BOTTOM. Identical set to the top (v4 §4). It renders
            LAST, after the Attachment | Note tabs, so it sits at the very bottom
            of the page — the same order Sample Registration uses, where its
            bottom sr-savebar follows <RegistrationTabs>. */}
        {renderActionBar('bottom')}
      </div>
    </div>
  );
}

/** Splits the legacy "; "-joined Oid list into an array, tolerating the
 *  stray spacing the legacy data contains. */
function splitIds(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(';').map(s => s.trim()).filter(Boolean);
}
